/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  CircularProgress,
  CardActionArea,
  CardHeader,
  Avatar,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  Alert,
} from "@mui/material";
import makeAnimated from "react-select/animated";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  convertUTCToLocal,
  formatDateTimeForAPI,
  getOffsetPosition,
} from "@/lib/utils/dateUtils";
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import Select from "react-select";
import useGetResource from "@/hooks/useGetResources";
import useGetDetailledObservation from "@/hooks/useGetDetailledObservation";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import useGetMetadata from "@/hooks/useGetMetaData";
import ObservationDetailsDialog from "./observationDetailsDialog";
import { useSession } from "@/hooks/useSession";
import DeleteModal from "@/components/deleteModal";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import MapIcon from "@mui/icons-material/Map";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { getMultiSelectStyles } from "@/lib/styles";
import PageHeader from "@/components/PageHeader";

import {
  APIProvider,
  AdvancedMarker,
  Map as GoogleMap,
  useMap,
  Pin,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Circle } from "@/components/circle";

const animatedComponents = makeAnimated();

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  transition: "box-shadow 0.2s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[6],
  },
  position: "relative",
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 10,
  borderRadius: theme.shape.borderRadius,
}));

const MapContainerStyle = {
  width: "100%",
  height: "600px",
};

const londonCenter = {
  lat: 51.5074,
  lng: -0.1278,
};

// Priority Color Mapping - uses priority code (P1, P2, P3) which is language-independent
const priorityColorMap = {
  P1: "#FF0000", // Red for High
  P2: "#FFA500", // Orange for Medium
  P3: "#008000", // Green for Low
};

// Helper function to extract priority code from priority name
function getPriorityColor(priorityName) {
  if (!priorityName) return "#FBBC04"; // Default yellow

  // Extract P1, P2, or P3 from the priority name (e.g., "P1 - High", "P1 - Yüksek", etc.)
  const match = priorityName.match(/^(P\d+)/i);
  if (match) {
    const code = match[1].toUpperCase();
    return priorityColorMap[code] || "#FBBC04";
  }

  return "#FBBC04"; // Default yellow
}

// PoiMarkers Component
function PoiMarkers({ pois, observations, onMarkerClick, t }) {
  const map = useMap();

  const [markers, setMarkers] = useState({});
  const [listeners, setListeners] = useState({});
  const clusterer = useRef(null);
  const [circleCenter, setCircleCenter] = useState(null);
  const [infoWindowPosition, setInfoWindowPosition] = useState(null);
  const [infoWindowObs, setInfoWindowObs] = useState([]);

  const obsByLocation = useMemo(() => {
    const grouped = {};
    observations.forEach((obs) => {
      if (
        Number.isFinite(obs.site?.latitude) &&
        Number.isFinite(obs.site?.longitude)
      ) {
        const key = `${obs.site.latitude},${obs.site.longitude}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(obs);
      }
    });
    return grouped;
  }, [observations]);

  const handleMarkerClick = useCallback(
    (obsId, lat, lng, obsAtLocation) => {
      map.panTo({ lat, lng });
      setCircleCenter({ lat, lng });
      if (obsAtLocation.length > 1) {
        setInfoWindowPosition({ lat, lng });
        setInfoWindowObs(obsAtLocation);
      } else {
        setInfoWindowPosition(null);
        setInfoWindowObs([]);
        onMarkerClick(obsId);
      }
    },
    [map, onMarkerClick]
  );

  const handleInfoWindowClose = () => {
    setInfoWindowPosition(null);
    setInfoWindowObs([]);
  };

  useEffect(() => {
    if (!map || clusterer.current) return;
    clusterer.current = new MarkerClusterer({ map });
  }, [map]);

  useEffect(() => {
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(
      Object.values(markers).filter((marker) => marker)
    );
  }, [markers]);

  const setMarkerRef = (marker, key) => {
    if (!key) {
      return;
    }
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      const newMarkers = { ...prev };
      if (marker) {
        newMarkers[key] = marker;
      } else {
        delete newMarkers[key];
      }
      return newMarkers;
    });

    setListeners((prev) => {
      const newListeners = { ...prev };
      if (marker) {
        const [, id, lat, lng] = key.split("|");
        const locKey = `${lat},${lng}`;
        const obsAtLocation = obsByLocation[locKey] || [];
        const listener = window.google.maps.event.addListener(
          marker,
          "gmp-click",
          () =>
            handleMarkerClick(
              id,
              parseFloat(lat),
              parseFloat(lng),
              obsAtLocation
            )
        );
        newListeners[key] = listener;
      } else {
        if (newListeners[key]) {
          window.google.maps.event.removeListener(newListeners[key]);
        }
        delete newListeners[key];
      }
      return newListeners;
    });
  };

  useEffect(() => {
    return () => {
      Object.values(listeners).forEach((listener) => {
        if (listener) {
          window.google.maps.event.removeListener(listener);
        }
      });
      clusterer.current?.clearMarkers();
    };
  }, []);

  return (
    <>
      <Circle
        radius={800}
        center={circleCenter}
        strokeColor="#0c4cb3"
        strokeOpacity={1}
        strokeWeight={3}
        fillColor="#3b82f6"
        fillOpacity={0.3}
      />
      {pois.map((poi) => {
        const obs = observations.find((o) => o.id === poi.key);
        if (!obs || !poi.key || !poi.location?.lat || !poi.location?.lng) {
          return null;
        }
        const locKey = `${poi.location.lat},${poi.location.lng}`;
        const obsAtLocation = obsByLocation[locKey] || [obs];
        const index = obsAtLocation.findIndex((o) => o.id === poi.key);
        const offsetPosition = getOffsetPosition(
          poi.location,
          index,
          obsAtLocation.length
        );
        const priorityColor = getPriorityColor(obs.observation_priority?.name);
        return (
          <AdvancedMarker
            key={`obs|${poi.key}|${poi.location.lat}|${poi.location.lng}`}
            position={offsetPosition}
            ref={(marker) =>
              setMarkerRef(
                marker,
                `obs|${poi.key}|${poi.location.lat}|${poi.location.lng}`
              )
            }
          >
            <Pin
              background={priorityColor}
              glyphColor="#000"
              borderColor="#000"
            />
          </AdvancedMarker>
        );
      })}
      {infoWindowPosition && infoWindowObs.length > 0 && (
        <InfoWindow
          position={infoWindowPosition}
          onCloseClick={handleInfoWindowClose}
        >
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {t("observationsPage.infoWindowTitle")}
            </Typography>
            {infoWindowObs.map((obs) => (
              <Typography
                key={obs.id}
                variant="body2"
                sx={{
                  cursor: "pointer",
                  color: "blue",
                  textDecoration: "underline",
                }}
                onClick={() =>
                  handleMarkerClick(
                    obs.id,
                    obs.site.latitude,
                    obs.site.longitude,
                    [obs]
                  )
                }
              >
                {obs.author.first_name} {obs.author.last_name} -{" "}
                {obs.observation_priority?.name ||
                  t("observationsPage.unknownPriority")}
              </Typography>
            ))}
          </Box>
        </InfoWindow>
      )}
    </>
  );
}

// ObservationCard Component
function ObservationCard({ obs, loading, onView, onDelete, t }) {
  const { session } = useSession();
  const theme = useTheme();

  if (!obs?.history_steps?.length) {
    return null;
  }

  const lastStep = obs.history_steps[obs.history_steps.length - 1];
  const rawStatus = lastStep?.status;
  const rawStatusDate = lastStep?.created_at;
  const latest = rawStatus
    ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()
    : t("observationsPage.unknownStatus");

  return (
    <StyledCard
      elevation={2}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          elevation: 8,
          borderColor: theme.palette.primary.main,
          transform: "translateY(-2px)",
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
      }}
    >
      {loading && (
        <LoadingOverlay>
          <CircularProgress size={32} color="primary" />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t("observationsPage.loadingDetails")}
          </Typography>
        </LoadingOverlay>
      )}

      <CardActionArea
        onClick={() => onView(obs.id)}
        sx={{
          flexGrow: 1,
          borderRadius: 3,
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              src={obs.author?.avatar_url}
              alt={obs.author?.username}
              sx={{
                width: 48,
                height: 48,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            />
          }
          title={
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: "1.1rem",
              }}
            >
              {obs.workflow?.name || t("observationsPage.noWorkflow")}
            </Typography>
          }
          subheader={
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {convertUTCToLocal(obs.created_at)}
            </Typography>
          }
          sx={{ pb: 1 }}
        />

        <CardContent sx={{ pt: 0, pb: 3 }}>
          <Stack spacing={2}>
            {/* Site and Priority Info */}
            <Box
              sx={{
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t("observationsPage.siteLabel")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {obs.site?.name || t("observationsPage.unknownSite")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t("observationsPage.priorityLabel")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {obs.observation_priority?.name ||
                      t("observationsPage.unknownPriority")}
                  </Typography>
                </Box>

                {obs.history_steps.length > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {t("observationsPage.createdAtLabel")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {convertUTCToLocal(obs.created_at)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Description */}
            {obs.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                }}
              >
                &ldquo;{obs.description}&rdquo;
              </Typography>
            )}

            {/* Timeline */}
            <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.5) }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {t("observationsPage.statusAtLabel", { status: latest })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {convertUTCToLocal(rawStatusDate)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>

      {session?.user_type === "ORG_ADMIN" && (
        <CardActions
          sx={{
            justifyContent: "flex-end",
            px: 2,
            py: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={() => onDelete(obs.id)}
            sx={{
              color: theme.palette.error.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </CardActions>
      )}
    </StyledCard>
  );
}

// ObservationsMap Component
function ObservationsMap({
  observations,
  markerLocations,
  mapsLoaded,
  onMarkerClick,
  t,
}) {
  const map = useMap();

  useEffect(() => {
    if (
      !map ||
      !mapsLoaded ||
      !window.google?.maps ||
      typeof window.google.maps.LatLngBounds !== "function" ||
      !markerLocations.length
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    markerLocations.forEach((poi) => {
      if (
        Number.isFinite(poi.location.lat) &&
        Number.isFinite(poi.location.lng)
      ) {
        bounds.extend({
          lat: poi.location.lat,
          lng: poi.location.lng,
        });
      }
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);
    } else {
      map.setCenter(londonCenter);
      map.setZoom(14);
    }
  }, [map, markerLocations, mapsLoaded]);

  return (
    <GoogleMap
      style={MapContainerStyle}
      defaultCenter={londonCenter}
      defaultZoom={14}
      mapId="YOUR_MAP_ID"
      zoomControl={true}
      gestureHandling="auto"
      minZoom={3}
      maxZoom={18}
    >
      <PoiMarkers
        pois={markerLocations}
        observations={observations}
        onMarkerClick={onMarkerClick}
        t={t}
      />
    </GoogleMap>
  );
}

export default function ObservationsPage() {
  const { t } = useTranslation();
  const { toast, setToast, handleToastClose } = useApiErrorHandler();
  const { mutateAsync: deleteData } = useDeleteData("/api/v1/observations", [
    "observations",
  ]);
  const queryClient = useQueryClient();
  const { getDetailledData } = useGetDetailledObservation();
  const {
    priorityList,
    SitesList,
    WorkFlows = [],
    isLoading: metadataLoading,
  } = useGetMetadata();
  const theme = useTheme();
  const multiSelectStyles = getMultiSelectStyles(theme);
  const { observationId: urlObservationId } = useParams();
  const navigate = useNavigate();
  const handledUrlObservationId = useRef(null);

  const observationWorkflowOptions = useMemo(
    () =>
      WorkFlows.filter((wf) => wf.target === "OBSERVATION").map((wf) => ({
        value: wf.id,
        label: wf.name,
      })),
    [WorkFlows]
  );

  // State declarations
  const [pageTokens, setPageTokens] = useState([null]);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedWorkflows, setSelectedWorkflows] = useState([]);
  const [selectedObs, setSelectedObs] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [initialTotalCount, setInitialTotalCount] = useState(null);

  // Fetch status filters
  const { data: statusFilters, isLoading: isStatusLoading } = useGetResource(
    ["statusFilters"],
    "/api/v1/observations/status-filters"
  );

  const statusButtons = useMemo(() => {
    if (!statusFilters) return [];
    return statusFilters.map((status) => ({
      name: status.name,
      count: status.count,
      ids: status.ids,
    }));
  }, [statusFilters]);

  // Define currentPageToken
  const currentPageToken = pageTokens[currentPageIndex];

  // Construct query URL based on selected statuses, priorities, sites, workflows, dates, and cursor
  const queryUrl = useMemo(() => {
    const baseUrl = "/api/v1/observations/search";
    const params = new URLSearchParams();

    if (selectedStatuses.length > 0) {
      selectedStatuses.forEach((status) => {
        status.ids.forEach((id) => params.append("statuses", id));
      });
    }
    if (selectedPriorities.length > 0) {
      selectedPriorities.forEach((priority) =>
        params.append("priorities", priority.value)
      );
    }
    if (selectedSites.length > 0) {
      selectedSites.forEach((site) => params.append("sites", site.value));
    }
    if (selectedWorkflows.length > 0) {
      selectedWorkflows.forEach((workflow) =>
        params.append("workflows", workflow.value)
      );
    }
    if (fromDate)
      params.append("from_datetime", formatDateTimeForAPI(fromDate));
    if (toDate) params.append("to_datetime", formatDateTimeForAPI(toDate));
    if (currentPageToken) params.append("cursor", currentPageToken);

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  }, [
    selectedStatuses,
    selectedPriorities,
    selectedSites,
    selectedWorkflows,
    fromDate,
    toDate,
    currentPageToken,
  ]);

  // Fetch observations
  const {
    data: Observations,
    isLoading,
    isFetching,
    error,
    refetch,
    isPlaceholderData,
    isError,
  } = useGetResource(
    [
      "observations",
      selectedStatuses.map((s) => s.name).join(","),
      selectedPriorities.map((p) => p.value).join(","),
      selectedSites.map((s) => s.value).join(","),
      selectedWorkflows.map((w) => w.value).join(","),
      fromDate,
      toDate,
      currentPageToken,
    ],
    queryUrl,
    { enabled: true }
  );

  useEffect(() => {
    if (isPlaceholderData) return;
    if (isError) return;
    if (!Observations || !Array.isArray(Observations.records)) return;

    const sanitized = Observations.records.filter((r) => r && r.id);

    if (currentPageToken == null) {
      setPages([sanitized]);
      setCurrentPageIndex(0);
      setInitialTotalCount(
        typeof Observations.total_count === "number"
          ? Observations.total_count
          : Number(Observations.total_count) || 0
      );
    } else {
      setPages((prev) => {
        const existingIds = new Set(
          prev.flat().filter(Boolean).map((r) => r.id).filter(Boolean)
        );
        const filteredRecords = sanitized.filter((r) => !existingIds.has(r.id));
        const newPages = [...prev];
        while (newPages.length <= currentPageIndex) {
          newPages.push([]);
        }
        newPages[currentPageIndex] = filteredRecords;
        return newPages;
      });
    }

    setNextPageToken(Observations.next_page ?? null);

    if (Observations.next_page) {
      setPageTokens((prev) => {
        if (prev.includes(Observations.next_page)) return prev;
        return [...prev, Observations.next_page];
      });
    }
  }, [
    Observations,
    currentPageIndex,
    currentPageToken,
    isPlaceholderData,
    isError,
  ]);

  const aggregatedObservations = useMemo(() => {
    const flat = pages
      .slice(0, currentPageIndex + 1)
      .flat()
      .filter((obs) => obs && obs.id);
    const seen = new Set();
    return flat.filter((obs) => {
      if (seen.has(obs.id)) return false;
      seen.add(obs.id);
      return true;
    });
  }, [pages, currentPageIndex]);

  const hasPartialResults =
    initialTotalCount != null &&
    initialTotalCount > 0 &&
    aggregatedObservations.length < initialTotalCount;

  const markerLocations = useMemo(() => {
    return aggregatedObservations
      .filter(
        (obs) =>
          Number.isFinite(obs.site?.latitude) &&
          Number.isFinite(obs.site?.longitude)
      )
      .map((obs) => ({
        key: obs.id,
        location: {
          lat: obs.site.latitude,
          lng: obs.site.longitude,
        },
      }));
  }, [aggregatedObservations]);

  // Handle direct URL access to observations
  useEffect(() => {
    if (
      urlObservationId &&
      handledUrlObservationId.current !== urlObservationId &&
      !selectedObs &&
      !loadingStates[urlObservationId]
    ) {
      handledUrlObservationId.current = urlObservationId;
      const fetchObservation = async () => {
        setLoadingStates((prev) => ({ ...prev, [urlObservationId]: true }));
        try {
          const data = await getDetailledData(urlObservationId);
          setSelectedObs(data);
        } catch (error) {
          const apiMsg = error.response?.data?.message;
          const fullMessage = apiMsg
            ? `${t("observationsPage.errorLoadDetails")}: ${apiMsg}`
            : t("observationsPage.errorLoadDetails");

          setToast({
            open: true,
            severity: "error",
            message: fullMessage,
          });
          // Navigate back to observations if direct URL access fails
          navigate("/observations", { replace: true });
        } finally {
          setLoadingStates((prev) => ({ ...prev, [urlObservationId]: false }));
        }
      };
      fetchObservation();
    }
  }, [
    urlObservationId,
    selectedObs,
    loadingStates,
    getDetailledData,
    setToast,
    t,
    navigate,
  ]);

  const handleStatusClick = (status) => {
    // Reset pagination first
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);

    setSelectedStatuses((prev) => {
      const isSelected = prev.some((s) => s.name === status.name);
      if (isSelected) {
        return prev.filter((s) => s.name !== status.name);
      }
      return [...prev, status];
    });
  };

  const handlePriorityChange = (selectedOptions) => {
    setSelectedPriorities(selectedOptions || []);
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);
  };

  const handleSiteChange = (selectedOptions) => {
    setSelectedSites(selectedOptions || []);
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);
  };

  const handleWorkflowChange = (selectedOptions) => {
    setSelectedWorkflows(selectedOptions || []);
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);
  };

  const handleResetFilters = () => {
    setFromDate("");
    setToDate("");
    setSelectedPriorities([]);
    setSelectedStatuses([]);
    setSelectedSites([]);
    setSelectedWorkflows([]);
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);
  };

  const handleNextPage = () => {
    if (!nextPageToken || isFetching) return;

    const slotEmpty =
      !pages[currentPageIndex] || pages[currentPageIndex]?.length === 0;
    if (currentPageIndex > 0 && slotEmpty && isError) {
      refetch();
      return;
    }

    const nextIdx = currentPageIndex + 1;
    if (nextIdx >= pageTokens.length) return;

    setCurrentPageIndex(nextIdx);
  };

  const handleDetailClick = async (observationId) => {
    setLoadingStates((prev) => ({ ...prev, [observationId]: true }));
    try {
      const data = await getDetailledData(observationId);
      setSelectedObs(data);
      // Update URL with observation ID
      navigate(`/observation/${observationId}`, { replace: false });
    } catch (error) {
      // Grab whichever message the API returned
      const apiMsg = error.response?.data?.message;

      // Your generic translation, then the API text
      const fullMessage = apiMsg
        ? `${t("observationsPage.errorLoadDetails")}: ${apiMsg}`
        : t("observationsPage.errorLoadDetails");

      setToast({
        open: true,
        severity: "error",
        message: fullMessage,
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [observationId]: false }));
    }
  };

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      const id = itemToDelete;
      setLoadingStates((prev) => ({ ...prev, [itemToDelete]: true }));
      try {
        await deleteData(itemToDelete);
        setToast({
          open: true,
          severity: "success",
          message: t("observationsPage.deleteSuccess"),
        });
        setPageTokens([null]);
        setPages([]);
        setCurrentPageIndex(0);
        refetch();
      } catch (err) {
        // Grab whichever message the API returned
        const apiMsg = err.response?.data?.message;
        const fullMsg = apiMsg
          ? `${t("observationsPage.deleteError")}: ${apiMsg}`
          : t("observationsPage.deleteError");

        setToast({
          open: true,
          severity: "error",
          message: fullMsg,
        });
      } finally {
        setLoadingStates((prev) => ({ ...prev, [id]: false }));
        setItemToDelete(null);
        setDeleteModalOpen(false);
      }
    }
  };

  const handleObservationDetailClose = useCallback(() => {
    setSelectedObs(null);
    handledUrlObservationId.current = null;
    refetch();
    // Navigate back to observations page when closing observation detail
    if (urlObservationId) {
      navigate("/observations", { replace: true });
    }
  }, [refetch, urlObservationId, navigate]);

  const handleOpenMapDialog = () => {
    setMapDialogOpen(true);
  };

  const handleCloseMapDialog = () => {
    setMapDialogOpen(false);
  };

  const hasMoreObservations = nextPageToken;

  return (
    <>
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />

      {/* 2) Then conditionally render the loading state or the main UI */}
      {isStatusLoading || metadataLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
          borderRadius={2}
        >
          <CircularProgress
            size={48}
            aria-label={t("observationsPage.loadingFiltersAria")}
          />
          <Typography variant="body1" ml={2} color="text.secondary">
            {t("observationsPage.loadingFilters")}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor: theme.palette.background.default,
          }}
        >
          {/* Compact Enterprise Header */}
          <PageHeader
            icon="🔍"
            title={t("observationsPage.title") || "Observations Management"}
            subtitle={
              t("observationsPage.subtitle") ||
              "Search, filter and manage observation reports"
            }
            stats={[
              {
                label: `${aggregatedObservations.length}/${
                  initialTotalCount ??
                  Observations?.total_count ??
                  aggregatedObservations.length
                }`,
              },
            ]}
            actions={[
              {
                label: t("observationsPage.viewMapButton") || "Map View",
                icon: <MapIcon />,
                onClick: handleOpenMapDialog,
                ariaLabel: t("observationsPage.viewMapAria"),
              },
            ]}
            t={t}
            translationPrefix="observationsPage"
            compact={true}
          />

          {hasPartialResults && (
            <Typography
              variant="caption"
              component="p"
              sx={{
                px: { xs: 2, sm: 3 },
                mb: 2,
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              {t("observationsPage.partialResultsNotice")}
            </Typography>
          )}

          {error && aggregatedObservations.length > 0 && (
            <Alert
              severity="error"
              sx={{ mb: 2, mx: { xs: 2, sm: 3 } }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => refetch()}
                  sx={{ fontWeight: 600, textTransform: "none" }}
                >
                  {t("observationsPage.retryButton")}
                </Button>
              }
            >
              {t("observationsPage.loadMoreErrorInline")}
            </Alert>
          )}

          {/* Enhanced Filters Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: 2,
              mb: 2,
            }}
          >
            <Accordion
              sx={{
                width: "100%",
                boxShadow: 2,
                borderRadius: 2,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                component="div"
                sx={{
                  minHeight: 48,
                  height: 48,
                  px: 2,
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}
                >
                  <ExpandMoreIcon sx={{ mr: 1 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t("observationsPage.searchFilters")}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    right: 16,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleResetFilters}
                    disabled={
                      !fromDate &&
                      !toDate &&
                      selectedPriorities.length === 0 &&
                      selectedStatuses.length === 0 &&
                      selectedSites.length === 0 &&
                      selectedWorkflows.length === 0
                    }
                  >
                    {t("observationsPage.resetFilters")}
                  </Button>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label={t("observationsPage.fromDateLabel")}
                          type="datetime-local"
                          size="normal"
                          value={fromDate}
                          onChange={(e) => {
                            setFromDate(e.target.value);
                            setPageTokens([null]);
                            setPages([]);
                            setCurrentPageIndex(0);
                            setInitialTotalCount(null);
                          }}
                          slotProps={{ inputLabel: { shrink: true } }}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label={t("observationsPage.toDateLabel")}
                          type="datetime-local"
                          size="normal"
                          value={toDate}
                          onChange={(e) => {
                            setToDate(e.target.value);
                            setPageTokens([null]);
                            setPages([]);
                            setCurrentPageIndex(0);
                            setInitialTotalCount(null);
                          }}
                          slotProps={{ inputLabel: { shrink: true } }}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Select
                      isMulti
                      components={animatedComponents}
                      options={priorityList}
                      value={selectedPriorities}
                      onChange={handlePriorityChange}
                      placeholder={t("observationsPage.selectPriorities")}
                      isClearable
                      styles={{
                        ...multiSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Select
                      isMulti
                      components={animatedComponents}
                      options={SitesList}
                      value={selectedSites}
                      onChange={handleSiteChange}
                      placeholder={t("observationsPage.selectSites")}
                      isClearable
                      styles={{
                        ...multiSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Select
                      isMulti
                      components={animatedComponents}
                      options={observationWorkflowOptions}
                      value={selectedWorkflows}
                      onChange={handleWorkflowChange}
                      placeholder={t("observationsPage.selectWorkflows")}
                      isClearable
                      styles={{
                        ...multiSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Enhanced Status Filters */}
          <Box
            sx={{
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              p: 2,
              mb: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[1],
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {t("observationsPage.statusFilters") || "Status Filters"}
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {statusButtons.map((status) => {
                const isSelected = selectedStatuses.some(
                  (s) => s.name === status.name
                );
                const isLoading = isFetching && isSelected;

                return (
                  <Button
                    key={status.name}
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => handleStatusClick(status)}
                    disabled={isLoading}
                    sx={{
                      minWidth: 120,
                      height: 48,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      position: "relative",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: theme.shadows[4],
                      },
                      ...(isSelected && {
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        boxShadow: theme.shadows[2],
                      }),
                      ...(isLoading && {
                        opacity: 0.7,
                      }),
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ mr: 1 }}
                      />
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="inherit"
                          sx={{ fontWeight: "inherit" }}
                        >
                          {status.name}
                        </Typography>
                        <Box
                          sx={{
                            height: 20,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            bgcolor: isSelected
                              ? alpha(theme.palette.primary.contrastText, 0.2)
                              : alpha(theme.palette.primary.main, 0.1),
                            color: isSelected
                              ? theme.palette.primary.contrastText
                              : theme.palette.primary.main,
                            border: "none",
                            borderRadius: "10px",
                            px: 1,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {status.count}
                        </Box>
                      </Stack>
                    )}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {isLoading && pages.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="50vh"
              borderRadius={2}
            >
              <CircularProgress
                size={48}
                aria-label={t("observationsPage.loadingObservationsAria")}
              />
              <Typography variant="body1" ml={2} color="text.secondary">
                {t("observationsPage.loadingObservations")}
              </Typography>
            </Box>
          )}

          {error && aggregatedObservations.length === 0 && !isFetching && (
            <Box
              p={3}
              bgcolor="error.light"
              borderRadius={2}
              textAlign="center"
            >
              <Typography variant="h6" color="error.main">
                {t("observationsPage.errorTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("observationsPage.errorMessage")}
              </Typography>
              <Button
                variant="contained"
                color="error"
                onClick={() => refetch()}
                sx={{ mt: 2, textTransform: "none" }}
              >
                {t("observationsPage.retryButton")}
              </Button>
            </Box>
          )}

          {aggregatedObservations.length === 0 && !isLoading && !error && (
            <Box p={3} borderRadius={2} textAlign="center">
              <Typography variant="h6" color="text.primary">
                {t("observationsPage.noObservationsTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("observationsPage.noObservationsMessage")}
              </Typography>
            </Box>
          )}

          {aggregatedObservations.length > 0 && (
            <Grid container spacing={3}>
              {aggregatedObservations.map((obs) => (
                <Grid
                  size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}
                  key={obs.id}
                >
                  <ObservationCard
                    obs={obs}
                    loading={!!loadingStates[obs.id]}
                    onView={handleDetailClick}
                    onDelete={handleDeleteClick}
                    t={t}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {hasMoreObservations && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 4,
                p: 3,
                bgcolor: theme.palette.background.paper,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={handleNextPage}
                disabled={!nextPageToken || isFetching}
                aria-label={t("observationsPage.loadMoreAria")}
                startIcon={
                  isFetching ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  boxShadow: theme.shadows[2],
                  "&:hover": {
                    boxShadow: theme.shadows[4],
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {isFetching
                  ? t("observationsPage.loading") || "Loading..."
                  : t("observationsPage.loadMore") || "Load More Observations"}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {selectedObs && (
        <ObservationDetailsDialog
          observation={selectedObs}
          onClose={handleObservationDetailClose}
          onActionSuccess={() => {
            setPageTokens([null]);
            setPages([]);
            setCurrentPageIndex(0);
            queryClient.invalidateQueries(["observations"]);
            setToast({
              open: true,
              severity: "success",
              message: t("observationsPage.actionSuccess"),
            });
          }}
        />
      )}

      <Dialog
        maxWidth="lg"
        open={mapDialogOpen}
        onClose={handleCloseMapDialog}
        sx={{ "& .MuiDialog-paper": { width: "800px", borderRadius: 2 } }}
      >
        <DialogTitle>
          {t("observationsPage.mapTitle")}
          <IconButton
            onClick={handleCloseMapDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <APIProvider
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
            libraries={["places"]}
            onLoad={() => setMapsLoaded(true)}
          >
            <ObservationsMap
              mapsLoaded={mapsLoaded}
              observations={aggregatedObservations}
              markerLocations={markerLocations}
              onClose={handleCloseMapDialog}
              onMarkerClick={handleDetailClick}
              t={t}
            />
          </APIProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMapDialog}>
            {t("observationsPage.closeButton")}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteModal
        isDeleteModalOpen={deleteModalOpen}
        setDeleteIsModalOpen={setDeleteModalOpen}
        handleDeleteItem={handleConfirmDelete}
        itemType={t("observationsPage.itemTypeObservation")}
      />
    </>
  );
}
